import "./meter.css";

// Output meter.
//
// Two visual styles share the same level/peak/clip logic:
//   - "bar"  (default) — thin recessed channel, ice-blue fill, amber pulse
//     on clipping. The one place design.md uses the amber accent.
//   - "led"  — a row of small LED dots whose colour graduates ice-blue →
//     green → yellow → orange → red across the meter's range. Each lit
//     dot glows in its own colour, identical recipe to the LED-style knob.
//     A peak-hold marker rides the dot nearest the recent peak with an
//     extra-bright halo.
//
// State (shared across styles):
//   - `level` (0..1) — current signal level, drives bar fill or dot count
//   - `peakHold` (0..1) — peak hold marker, decays slowly

export type MeterStyle = "bar" | "led";

export interface MeterOptions {
  container: HTMLElement;
  /** Track length in CSS px. Default 160. */
  width?: number;
  /** Track height for the bar variant. Default 8. Ignored by the LED
   *  variant (its visible height is determined by the dot geometry). */
  height?: number;
  /** Threshold at which the clip indicator engages. Default 0.99. */
  clipThreshold?: number;
  label?: string;
  /** Visual style. Default "bar". */
  style?: MeterStyle;
  /** LED dot count (only used when `style === "led"`). Default 16. */
  ledCount?: number;
}

const PEAK_FALL_PER_MS = 0.0008; // ~1.25 s from 1.0 → 0

/** Canvas padding around the visible meter, in CSS px, for the LED style.
 *  Reserves room for each dot's shadow halo to bleed past the meter's
 *  visible bounds without being clipped at the canvas edge. Sized to
 *  comfortably exceed the peak-marker's 16px shadow blur radius. */
const LED_CANVAS_PAD = 24;

/** LED dot radius and unlit radius (matches the LED knob's ring-dot scale). */
const DOT_RADIUS = 3;
const DOT_UNLIT_RADIUS = 1.8;

/** Vertical height of the LED canvas content area (between top/bottom pad). */
const LED_INNER_HEIGHT = 14;

/** Time constant for the per-dot fade in / fade out. ~80ms gives a
 *  perceived fade of around 200ms — quick enough to feel responsive,
 *  slow enough to take the edge off the binary on/off pop. */
const DOT_GLOW_TAU_MS = 80;

type Rgb = readonly [number, number, number];

/** Number of dots at the high end painted red to indicate clipping zone. */
const CLIP_DOT_COUNT = 2;

const LED_BLUE: Rgb = [125, 211, 252]; // ice blue   #7dd3fc
const LED_RED: Rgb = [239, 68, 68]; //    danger red #ef4444
const UNLIT_RGB: Rgb = [124, 135, 148];
const UNLIT_ALPHA = 0.4;

/** Per-dot colour. Dots in the top `CLIP_DOT_COUNT` indices are red (clip
 *  zone); everything else is the standard ice blue. No gradient — the
 *  meter reads as "all blue normally, hits red when you peg". */
function ledDotColor(i: number, count: number): Rgb {
  return i >= count - CLIP_DOT_COUNT ? LED_RED : LED_BLUE;
}

function rgbaStr(c: Rgb, a: number): string {
  return `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${a})`;
}

export class Meter {
  private root: HTMLDivElement;

  // Bar style nodes.
  private fill: HTMLDivElement | null = null;
  private peakEl: HTMLDivElement | null = null;

  // LED style nodes.
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  private style: MeterStyle;
  private ledCount: number;
  private width: number;
  private height: number;
  private clipThreshold: number;

  private level = 0;
  private peakHold = 0;
  private clipTime = 0;
  private lastFrame = 0;
  private rafHandle = 0;
  private dirty = false;

  /** Per-dot rendered brightness (0..1), lerping toward target each frame.
   *  Target is 1 when `level >= threshold_i`, else 0. The lerp is what
   *  produces the fade in / fade out — without it, dots pop binary. */
  private dotGlow: number[] = [];
  /** Per-dot peak-marker contribution (0..1). Target is 1 when `i ===
   *  peakIdx`, else 0. Drives the extra halo on the peak dot and smoothly
   *  cross-fades when the peak position steps down through dots during
   *  peakHold decay. */
  private peakBoost: number[] = [];
  private lastLedFrame = 0;

  constructor(opts: MeterOptions) {
    this.style = opts.style ?? "bar";
    this.ledCount = opts.ledCount ?? 16;
    this.width = opts.width ?? 160;
    this.height = opts.height ?? 8;
    this.clipThreshold = opts.clipThreshold ?? 0.99;

    this.root = document.createElement("div");
    this.root.className = "meter";
    if (this.style === "led") this.root.classList.add("is-led");

    if (opts.label) {
      const lbl = document.createElement("span");
      lbl.className = "label";
      lbl.textContent = opts.label;
      this.root.append(lbl);
    }

    if (this.style === "led") {
      this.buildLedNodes();
    } else {
      this.buildBarNodes();
    }

    opts.container.append(this.root);
    this.render();
  }

  /** Set the current signal level (0..1). Drives both the visible level
   *  indicator and the peak-hold marker (which ratchets upward, then decays). */
  setLevel(level: number): void {
    const v = Math.max(0, Math.min(1, level));
    if (v === this.level) {
      this.dirty = true; // peak still decays
    } else {
      this.level = v;
      this.dirty = true;
    }
    if (v >= this.clipThreshold) {
      this.clipTime = performance.now();
    }
    if (v > this.peakHold) {
      this.peakHold = v;
    }
    this.ensureRaf();
  }

  destroy(): void {
    cancelAnimationFrame(this.rafHandle);
    this.root.remove();
  }

  // ----- DOM construction -----

  private buildBarNodes(): void {
    const track = document.createElement("div");
    track.className = "meter-track";
    track.style.width = `${this.width}px`;
    track.style.height = `${this.height}px`;

    const fill = document.createElement("div");
    fill.className = "meter-fill";

    const peakEl = document.createElement("div");
    peakEl.className = "meter-peak";

    track.append(fill, peakEl);
    this.root.append(track);
    this.fill = fill;
    this.peakEl = peakEl;
  }

  private buildLedNodes(): void {
    const canvas = document.createElement("canvas");
    canvas.className = "meter-led-canvas";
    const wCss = this.width + 2 * LED_CANVAS_PAD;
    const hCss = LED_INNER_HEIGHT + 2 * LED_CANVAS_PAD;
    canvas.style.width = `${wCss}px`;
    canvas.style.height = `${hCss}px`;
    // Negative margins absorb the canvas padding so the meter occupies the
    // same visible footprint as the bar variant in layouts.
    canvas.style.margin = `-${LED_CANVAS_PAD}px -${LED_CANVAS_PAD}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("meter: 2d context unavailable");
    this.canvas = canvas;
    this.ctx = ctx;
    this.root.append(canvas);
  }

  // ----- frame loop -----

  private ensureRaf(): void {
    if (this.rafHandle) return;
    this.lastFrame = performance.now();
    const step = (now: number): void => {
      const dt = now - this.lastFrame;
      this.lastFrame = now;
      this.peakHold = Math.max(
        this.level,
        this.peakHold - PEAK_FALL_PER_MS * dt,
      );
      this.render();
      const sinceClip = now - this.clipTime;
      const stillBusy =
        this.peakHold > this.level + 1e-3 ||
        sinceClip < 600 ||
        this.ledSettling();
      if (stillBusy || this.dirty) {
        this.dirty = false;
        this.rafHandle = requestAnimationFrame(step);
      } else {
        this.rafHandle = 0;
      }
    };
    this.rafHandle = requestAnimationFrame(step);
  }

  // ----- rendering -----

  private render(): void {
    if (this.style === "led") {
      this.drawLed();
    } else {
      this.drawBar();
    }
  }

  private drawBar(): void {
    if (!this.fill || !this.peakEl) return;
    this.fill.style.width = `${this.level * 100}%`;
    this.peakEl.style.left = `${this.peakHold * 100}%`;
    const clipping = performance.now() - this.clipTime < 500;
    this.root.classList.toggle("is-clipping", clipping);
  }

  private drawLed(): void {
    const ctx = this.ctx;
    const canvas = this.canvas;
    if (!ctx || !canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const wCss = this.width + 2 * LED_CANVAS_PAD;
    const hCss = LED_INNER_HEIGHT + 2 * LED_CANVAS_PAD;
    const px = wCss * dpr;
    const py = hCss * dpr;
    if (canvas.width !== px || canvas.height !== py) {
      canvas.width = px;
      canvas.height = py;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, wCss, hCss);
    ctx.translate(LED_CANVAS_PAD, LED_CANVAS_PAD);

    const dotY = LED_INNER_HEIGHT / 2;
    const usableW = this.width - 2 * DOT_RADIUS;
    const count = this.ledCount;

    // Lazy init of the per-dot glow channels — first render after the
    // canvas exists is also the first time we know the count.
    while (this.dotGlow.length < count) this.dotGlow.push(0);
    while (this.peakBoost.length < count) this.peakBoost.push(0);

    // Advance both glow channels toward their targets with the fade τ.
    // dt is capped so an idle period followed by a re-draw can't produce
    // a one-frame jump that defeats the fade.
    const now = performance.now();
    const rawDt = this.lastLedFrame > 0 ? now - this.lastLedFrame : 16;
    const dt = Math.min(100, rawDt);
    this.lastLedFrame = now;
    const coef = DOT_GLOW_TAU_MS > 0 ? 1 - Math.exp(-dt / DOT_GLOW_TAU_MS) : 1;

    const peakIdx =
      this.peakHold > 1e-3
        ? Math.max(0, Math.min(count - 1, Math.ceil(this.peakHold * count) - 1))
        : -1;
    for (let i = 0; i < count; i++) {
      const threshold = (i + 1) / count;
      const litTarget = this.level >= threshold ? 1 : 0;
      this.dotGlow[i]! += (litTarget - this.dotGlow[i]!) * coef;
      const peakTarget = i === peakIdx ? 1 : 0;
      this.peakBoost[i]! += (peakTarget - this.peakBoost[i]!) * coef;
    }

    // Clipping linger window — boost shadow blur on lit dots while a
    // recent clip event is still within ~500ms so the meter "flashes"
    // without us needing a separate animation channel.
    const sinceClip = performance.now() - this.clipTime;
    const clipBoost = sinceClip < 500 ? 1 + (1 - sinceClip / 500) * 0.6 : 1;

    // Single render pass per dot. Both glow channels combine into one
    // visible brightness; the peak channel additionally pushes shadow blur
    // wider, so the peak dot wears a bigger halo without breaking the
    // uniform fill colour the user asked for.
    for (let i = 0; i < count; i++) {
      const norm = i / (count - 1);
      const x = DOT_RADIUS + norm * usableW;
      const lit = this.dotGlow[i]!;
      const peak = this.peakBoost[i]!;
      const bright = Math.max(lit, peak);
      const litRgb = ledDotColor(i, count);

      const r = Math.round(
        UNLIT_RGB[0] + (litRgb[0] - UNLIT_RGB[0]) * bright,
      );
      const g = Math.round(
        UNLIT_RGB[1] + (litRgb[1] - UNLIT_RGB[1]) * bright,
      );
      const b = Math.round(
        UNLIT_RGB[2] + (litRgb[2] - UNLIT_RGB[2]) * bright,
      );
      const alpha = UNLIT_ALPHA + (1 - UNLIT_ALPHA) * bright;
      const radius = DOT_UNLIT_RADIUS + (DOT_RADIUS - DOT_UNLIT_RADIUS) * bright;

      ctx.save();
      if (bright > 1e-3) {
        ctx.shadowColor = rgbaStr(litRgb, 0.85 * bright);
        // Base halo from the lit state plus an extra ~8px for the peak.
        ctx.shadowBlur = (8 * bright + 8 * peak) * clipBoost;
      }
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(x, dotY, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  /** True if any LED glow channel is still mid-transition. The raf loop
   *  needs to know so it can keep ticking until the fade settles, even
   *  when no new `setLevel` arrives. */
  private ledSettling(): boolean {
    if (this.style !== "led") return false;
    const count = this.ledCount;
    const peakIdx =
      this.peakHold > 1e-3
        ? Math.max(0, Math.min(count - 1, Math.ceil(this.peakHold * count) - 1))
        : -1;
    for (let i = 0; i < count; i++) {
      const threshold = (i + 1) / count;
      const litTarget = this.level >= threshold ? 1 : 0;
      if (Math.abs((this.dotGlow[i] ?? 0) - litTarget) > 1e-3) return true;
      const peakTarget = i === peakIdx ? 1 : 0;
      if (Math.abs((this.peakBoost[i] ?? 0) - peakTarget) > 1e-3) return true;
    }
    return false;
  }
}
